/**
 * Native cart drawer behaviour: rewards progress bar, free gift with purchase,
 * and the in-drawer upsell carousel that replaces the third-party upsell app.
 *
 * The drawer is re-rendered server side on every cart change, so almost all
 * state lives in Liquid. This file only covers what Liquid cannot do:
 *   - adding / removing the free gift line when the threshold is crossed
 *   - fetching Product Recommendations for the upsell block
 *   - adding an upsell product to the cart
 *
 * The theme's own drawer blocks keep themselves in sync by listening for the
 * `update-nodes` event that `cart-drawer` emits with freshly rendered markup;
 * the components here follow the same contract.
 */
(function () {
  'use strict';

  var GIFT_PROPERTY = '_cuft_free_gift';
  var COVERAGE_PROPERTY = '_cuft_coverage';
  var CART_DRAWER_SELECTOR = 'cart-drawer';

  /* ------------------------------------------------------------------ *
   * Cart helpers
   * ------------------------------------------------------------------ */

  function jsonRequest(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        // Shopify answers 4xx cart errors with a JSON body, not a rejection.
        // Surfacing them as rejections keeps callers from treating a failed
        // write as a successful one and retrying forever.
        if (data && (data.status || data.errors)) {
          throw new Error(data.description || data.message || 'Cart request failed');
        }
        return data;
      });
  }

  function getCart() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (response) {
        return response.json();
      })
      .catch(function () {
        return null;
      });
  }

  /** Data attributes carrying JSON: never let a malformed one break a render. */
  function parseJSONAttribute(element, key, fallback) {
    try {
      return JSON.parse(element.dataset[key]);
    } catch (error) {
      return fallback;
    }
  }

  function delay(ms) {
    return new Promise(resolve => window.setTimeout(resolve, ms));
  }

  function isGiftLine(item) {
    return Boolean(item && item.properties && item.properties[GIFT_PROPERTY]);
  }

  /**
   * Return coverage is a fee, not spend, so it never counts towards a reward
   * threshold. Matched on the product as well as the property, so a line left
   * behind by Loop's own app — which sets no property — is still discounted.
   */
  function isCoverageLine(item, coverageProductId) {
    if (!item) {
      return false;
    }
    if (item.properties && item.properties[COVERAGE_PROPERTY]) {
      return true;
    }
    return Boolean(coverageProductId) && item.product_id === coverageProductId;
  }

  function addToCart(variantId, properties) {
    var line = { id: variantId, quantity: 1 };
    if (properties) {
      line.properties = properties;
    }
    return jsonRequest('/cart/add.js', { items: [line] });
  }

  function changeLine(key, quantity) {
    return jsonRequest('/cart/change.js', { id: key, quantity: quantity });
  }

  /**
   * Re-render the drawer from the Section Rendering API and hand the fresh
   * markup to whichever block components are currently mounted. When the drawer
   * has never been opened its children live in a template, so we swap the
   * element the sidebar will mount instead.
   */
  function refreshDrawer() {
    document.dispatchEvent(new CustomEvent('cart:updated'));

    var drawer = document.querySelector(CART_DRAWER_SELECTOR);
    var sectionId = drawer && drawer.dataset.sectionId;
    if (!drawer || !sectionId) {
      return Promise.resolve();
    }

    var url = window.location.pathname + '?sections=' + encodeURIComponent(sectionId);

    // Wait for the upgrade: an un-upgraded <cart-drawer> would have its
    // `element` overwritten by its own constructor a moment later.
    return window.customElements
      .whenDefined(CART_DRAWER_SELECTOR)
      .then(function () {
        return fetch(url, { credentials: 'same-origin' });
      })
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        var markup = data && data[sectionId];
        if (!markup) {
          return;
        }

        var doc = new DOMParser().parseFromString(markup, 'text/html');
        var template = doc.querySelector('[data-sidebar-template="' + drawer.id + '"]');
        var node = template && template.content ? template.content.firstElementChild : null;
        if (!node) {
          return;
        }

        // While the drawer is closed its body still sits inside the <template>,
        // where querySelector cannot reach it — and none of the block components
        // are mounted, so there is nobody to receive `update-nodes`. In that case
        // swap the element the sidebar will mount on the next open instead.
        var isMounted = drawer.querySelector('[data-sidebar-body]') !== null;

        if (isMounted && typeof drawer.emit === 'function') {
          // A line item added above the fold grows the results block and shoves
          // whatever the customer was looking at down the page. Measure first,
          // then put the scroll position back by however much it grew.
          var viewport = drawer.querySelector('[data-cart-drawer-viewport]');
          var results = drawer.querySelector('[data-cart-drawer-results]');
          var scrollTop = viewport ? viewport.scrollTop : 0;
          var heightBefore = results ? results.offsetHeight : 0;

          drawer.emit('update-nodes', { node: node });
          if (typeof drawer.updateEmptyStatus === 'function') {
            drawer.updateEmptyStatus();
          }

          if (viewport && results && scrollTop > 0) {
            var grew = results.offsetHeight - heightBefore;
            if (grew) {
              viewport.scrollTop = scrollTop + grew;
            }
          }
        } else {
          drawer.element = node;
        }
      })
      .catch(function () {
        /* Leave the drawer showing its last good state. */
      });
  }

  /* ------------------------------------------------------------------ *
   * Free gift with purchase
   * ------------------------------------------------------------------ */

  var giftBusy = false;
  var giftQueued = false;
  /** Safety valve: stop retrying if adding the gift never sticks. */
  var giftAddAttempts = 0;
  var GIFT_ADD_ATTEMPT_LIMIT = 3;

  /**
   * Bring the cart in line with the configured gift threshold: add the gift
   * once the qualifying total is reached, take it back out when the cart drops
   * below it. The gift's own price never counts towards the threshold.
   */
  function reconcileGift() {
    var element = document.querySelector('cuft-cart-gift');
    if (!element) {
      return Promise.resolve();
    }

    // With no gift configured there is nothing to add, but a gift left over from
    // a since-cleared setting still has to come back out of the cart.
    var variantId = Number(element.dataset.variantId || 0);
    var threshold = Number(element.dataset.thresholdCents || 0);
    var coverageProductId = Number(element.dataset.coverageProductId || 0);
    var isConfigured = Boolean(variantId) && threshold > 0;

    if (giftBusy) {
      giftQueued = true;
      return Promise.resolve();
    }
    giftBusy = true;

    return getCart()
      .then(function (cart) {
        if (!cart || !Array.isArray(cart.items)) {
          return false;
        }

        var giftLines = cart.items.filter(isGiftLine);
        var qualifyingTotal = cart.items.reduce(function (total, item) {
          if (isGiftLine(item) || isCoverageLine(item, coverageProductId)) {
            return total;
          }
          return total + item.final_line_price;
        }, 0);

        if (!isConfigured || qualifyingTotal < threshold) {
          return removeLines(giftLines).then(function () {
            return giftLines.length > 0;
          });
        }

        var keeper = giftLines.filter(function (line) {
          return line.variant_id === variantId;
        })[0];
        var stale = giftLines.filter(function (line) {
          return line !== keeper;
        });

        return removeLines(stale).then(function () {
          if (!keeper) {
            if (giftAddAttempts >= GIFT_ADD_ATTEMPT_LIMIT) {
              return stale.length > 0;
            }
            giftAddAttempts += 1;
            return addToCart(variantId, wrapGiftProperty()).then(function () {
              return true;
            });
          }
          giftAddAttempts = 0;
          if (keeper.quantity !== 1) {
            return changeLine(keeper.key, 1).then(function () {
              return true;
            });
          }
          return stale.length > 0;
        });
      })
      .catch(function () {
        return false;
      })
      .then(function (didChange) {
        return didChange ? refreshDrawer() : null;
      })
      .then(function () {
        giftBusy = false;
        if (giftQueued) {
          giftQueued = false;
          return reconcileGift();
        }
        return null;
      });
  }

  function wrapGiftProperty() {
    var properties = {};
    properties[GIFT_PROPERTY] = 'true';
    return properties;
  }

  /** Cart writes have to be sequential — Shopify rejects concurrent changes. */
  function removeLines(lines) {
    return lines.reduce(function (chain, line) {
      return chain.then(function () {
        return changeLine(line.key, 0);
      });
    }, Promise.resolve());
  }

  /* ------------------------------------------------------------------ *
   * Components
   * ------------------------------------------------------------------ */

  /**
   * Shared plumbing for a drawer block: subscribe to the drawer's `update-nodes`
   * event and swap in this block's freshly rendered markup. The theme's own
   * blocks do exactly this, which is why a block that skips it silently goes
   * stale after the first cart change.
   */
  class CuftDrawerBlock extends HTMLElement {
    connectedCallback() {
      this.handleDrawerUpdate = this.handleDrawerUpdate.bind(this);
      this.bindDrawer();
      this.mounted();
    }

    disconnectedCallback() {
      if (this.drawer && typeof this.drawer.off === 'function') {
        this.drawer.off('update-nodes', this.handleDrawerUpdate);
      }
      this.isSubscribed = false;
      this.unmounted();
    }

    mounted() {}

    unmounted() {}

    updated() {}

    bindDrawer() {
      const drawer = this.closest(CART_DRAWER_SELECTOR);
      if (!drawer) {
        return;
      }
      this.drawer = drawer;

      // The subscription can be deferred until `cart-drawer` upgrades, so guard
      // against a remount in between resolving into a duplicate listener.
      const subscribe = () => {
        if (!this.isConnected || this.isSubscribed || typeof drawer.on !== 'function') {
          return;
        }
        drawer.on('update-nodes', this.handleDrawerUpdate);
        this.isSubscribed = true;
      };

      if (typeof drawer.on === 'function') {
        subscribe();
      } else {
        window.customElements.whenDefined(CART_DRAWER_SELECTOR).then(subscribe);
      }
    }

    /** Locate this same block inside freshly rendered markup. */
    findFreshNode(scope) {
      const blockId = this.getAttribute('block-id');
      const tagName = this.tagName.toLowerCase();
      const selector = blockId ? `${tagName}[block-id="${blockId}"]` : tagName;
      return scope.querySelector(selector);
    }

    handleDrawerUpdate(data) {
      const fresh = data && data.node ? this.findFreshNode(data.node) : null;
      if (!fresh) {
        return;
      }
      this.replaceChildren(...fresh.childNodes);
      this.updated(fresh);
    }
  }

  /** Progress bar and review are pure markup — the base swap is all they need. */
  class CuftCartProgressBlock extends CuftDrawerBlock {}
  class CuftCartReviewBlock extends CuftDrawerBlock {}

  class CuftCartUpsellBlock extends CuftDrawerBlock {
    mounted() {
      this.handleClick = this.handleClick.bind(this);
      this.handleChange = this.handleChange.bind(this);
      this.handleScroll = this.handleScroll.bind(this);

      this.addEventListener('click', this.handleClick);
      this.addEventListener('change', this.handleChange);
      window.addEventListener('resize', this.handleScroll);

      this.loadRecommendations();
      // syncReadyState owns the track's scroll listener, since the track is
      // replaced every time the cards are.
      this.syncReadyState();
    }

    unmounted() {
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('change', this.handleChange);
      window.removeEventListener('resize', this.handleScroll);
      this.boundTrack?.removeEventListener('scroll', this.handleScroll);
      this.boundTrack = null;
    }

    handleScroll() {
      window.clearTimeout(this.scrollTimer);
      this.scrollTimer = window.setTimeout(() => this.syncActiveSlide(), 60);
    }

    /**
     * Recommendation markup only exists on the recommendations route, so the
     * freshly rendered drawer carries an empty block. Swapping it in would blank
     * the carousel until the refetch lands, so for that source we keep the
     * current cards and only refresh the request URL.
     */
    handleDrawerUpdate(data) {
      const fresh = data && data.node ? this.findFreshNode(data.node) : null;
      if (!fresh) {
        return;
      }

      if (this.dataset.source === 'recommendations') {
        this.dataset.url = fresh.dataset.url || '';
        this.loadRecommendations();
        return;
      }

      this.replaceChildren(...fresh.childNodes);
      this.syncReadyState();
    }

    /**
     * The recommendations route re-renders this section with the
     * `recommendations` object populated, so we pull our own block back out of
     * that response rather than building cards in JS.
     */
    loadRecommendations() {
      if (this.dataset.source !== 'recommendations') {
        return;
      }

      // A cart change remounts the block and emits `update-nodes`; coalesce so
      // that lands as one request.
      window.clearTimeout(this.loadTimer);
      this.loadTimer = window.setTimeout(() => this.fetchRecommendations(), 50);
    }

    fetchRecommendations() {
      const url = this.dataset.url;
      if (!url) {
        this.replaceChildren();
        this.syncReadyState();
        return;
      }

      fetch(url, { credentials: 'same-origin' })
        .then(response => response.text())
        .then(text => {
          const doc = new DOMParser().parseFromString(text, 'text/html');
          // The drawer ships inside a <template>, whose content querySelector
          // will not descend into from the document itself.
          const template = doc.querySelector('[data-sidebar-template]');
          const scope = template && template.content ? template.content : doc;
          const fresh = this.findFreshNode(scope);
          if (fresh) {
            this.replaceChildren(...fresh.childNodes);
          }
        })
        .catch(() => {
          /* No recommendations: the block stays hidden. */
        })
        .then(() => this.syncReadyState());
    }

    /** Nothing to offer means nothing to show. */
    syncReadyState() {
      this.toggleAttribute('is-ready', this.querySelector('[data-cuft-upsell-card]') !== null);

      // The track element is replaced whenever the cards are, so its scroll
      // listener has to be reattached to the new one.
      const track = this.querySelector('[data-cuft-upsell-track]');
      if (track && track !== this.boundTrack) {
        this.boundTrack?.removeEventListener('scroll', this.handleScroll);
        track.addEventListener('scroll', this.handleScroll, { passive: true });
        this.boundTrack = track;
      }

      this.syncAllCards();
      this.syncActiveSlide();
    }

    handleClick(event) {
      const dot = event.target.closest('[data-cuft-upsell-dot]');
      if (dot) {
        this.scrollToCard(Number(dot.dataset.cuftUpsellDot));
        return;
      }

      const button = event.target.closest('[data-cuft-upsell-add]');
      if (button) {
        this.addUpsell(button);
      }
    }

    /* ---------------------------------------------------------------- *
     * Variant selection
     * ---------------------------------------------------------------- */

    handleChange(event) {
      const select = event.target.closest('[data-cuft-upsell-option]');
      if (!select) {
        return;
      }

      // <cart-drawer> listens for `change` on itself to drive quantity edits,
      // and reads target.dataset.index off whatever fired. An option dropdown
      // has no index, so the drawer sets .is-cart-loading and then bails out of
      // updateItem before clearing it — which leaves the class stuck and
      // pointer-events: none on every line item's remove button. Keep our own
      // control changes away from it.
      event.stopPropagation();

      this.syncCard(select.closest('[data-cuft-upsell-card]'));
    }

    /** Every card, so a freshly rendered carousel starts consistent. */
    syncAllCards() {
      this.querySelectorAll('[data-cuft-upsell-card]').forEach(card => this.syncCard(card));
    }

    /**
     * Resolve the chosen options back to a variant and bring the card's price,
     * swatch and add button in line with it.
     */
    syncCard(card) {
      if (!card) {
        return;
      }

      const variants = parseJSONAttribute(card, 'variants', []);
      const selects = [...card.querySelectorAll('[data-cuft-upsell-option]')];
      const button = card.querySelector('[data-cuft-upsell-add]');

      this.syncSwatches(card);

      if (!button || !variants.length) {
        return;
      }

      // With no dropdowns the card is a single-variant offer and the button
      // already carries the right id.
      const chosen = selects.length
        ? variants.find(variant =>
            selects.every(
              select => variant.options[Number(select.dataset.cuftUpsellOption)] === select.value
            )
          )
        : variants.find(variant => String(variant.id) === button.dataset.variantId);

      this.syncPrice(card, chosen);

      const isAvailable = Boolean(chosen && chosen.available);
      button.dataset.variantId = chosen ? String(chosen.id) : '';
      button.disabled = !isAvailable;

      const label = button.querySelector('[data-cuft-upsell-add-label]');
      if (label) {
        label.textContent = isAvailable
          ? button.dataset.defaultLabel || 'Add'
          : button.dataset.soldOutLabel || 'Sold out';
      }
    }

    syncPrice(card, variant) {
      const now = card.querySelector('[data-cuft-upsell-price-now]');
      const was = card.querySelector('[data-cuft-upsell-price-was]');
      if (!now || !variant) {
        return;
      }

      now.textContent = variant.price;

      if (was) {
        was.textContent = variant.onSale ? variant.compareAtPrice : '';
        was.toggleAttribute('hidden', !variant.onSale);
      }
    }

    syncSwatches(card) {
      card.querySelectorAll('[data-cuft-upsell-color]').forEach(select => {
        const swatch = select.closest('.cuft-upsell__option').querySelector('[data-cuft-upsell-swatch]');
        if (!swatch) {
          return;
        }
        const swatches = parseJSONAttribute(select, 'swatches', {});
        swatch.style.background = swatches[select.value] || 'transparent';
      });
    }

    /* ---------------------------------------------------------------- *
     * Carousel
     * ---------------------------------------------------------------- */

    scrollToCard(index) {
      const track = this.querySelector('[data-cuft-upsell-track]');
      const card = this.querySelectorAll('[data-cuft-upsell-card]')[index];
      if (!track || !card) {
        return;
      }
      track.scrollTo({ left: card.offsetLeft - track.offsetLeft, behavior: 'smooth' });
    }

    /**
     * Whichever card is nearest the track's left edge is the active one: it gets
     * the filled dot, and the track takes its height. Cards are no longer
     * stretched to a common height, so a short card no longer has to carry the
     * tallest one's whitespace.
     */
    syncActiveSlide() {
      const track = this.querySelector('[data-cuft-upsell-track]');
      const cards = [...this.querySelectorAll('[data-cuft-upsell-card]')];
      if (!track || !cards.length) {
        return;
      }

      let active = 0;
      let smallest = Infinity;

      cards.forEach((card, index) => {
        const distance = Math.abs(card.offsetLeft - track.offsetLeft - track.scrollLeft);
        if (distance < smallest) {
          smallest = distance;
          active = index;
        }
      });

      this.querySelectorAll('[data-cuft-upsell-dot]').forEach((dot, index) => {
        dot.toggleAttribute('is-active', index === active);
      });

      track.style.height = `${cards[active].offsetHeight}px`;
    }

    addUpsell(button) {
      if (button.disabled) {
        return;
      }

      // syncCard keeps this in step with the option dropdowns.
      const variantId = Number(button.dataset.variantId);
      if (!variantId) {
        return;
      }

      const label = button.querySelector('[data-cuft-upsell-add-label]');
      button.disabled = true;
      // `loading` is the theme's own class: it hides the button's spans and
      // fades in the .btn__loading-spinner sitting inside it.
      button.classList.add('loading');

      const plus = button.querySelector('.cuft-upsell__add-plus');

      addToCart(variantId)
        .then(() => {
          // Refreshing straight away swaps the card out in the same frame the
          // button was clicked, which reads as the card vanishing rather than
          // as a confirmation. Hold the confirmed state briefly first.
          button.classList.remove('loading');
          button.classList.add('is-added');
          if (label) {
            label.textContent = button.dataset.addedLabel || 'Added';
          }
          if (plus) {
            plus.textContent = '\u2713';
          }
          return delay(800).then(() => refreshDrawer()).then(reconcileGift);
        })
        .catch(() => {
          if (label) {
            label.textContent = button.dataset.defaultLabel || 'Add';
          }
          if (plus) {
            plus.textContent = '+';
          }
          button.classList.remove('is-added');
        })
        .then(() => {
          button.disabled = false;
          button.classList.remove('loading');
        });
    }
  }

  /**
   * Owns the free gift. It sits outside `<cart-drawer>` so it stays mounted even
   * while the drawer is closed, and reconciles on every cart change.
   */
  class CuftCartGift extends HTMLElement {
    connectedCallback() {
      this.handleCartUpdate = () => reconcileGift();
      this.bindDrawer();
      document.addEventListener('cart:updated', this.handleCartUpdate);
      // Catch carts that already qualified before this page was loaded.
      window.setTimeout(() => {
        if (this.isConnected) {
          reconcileGift();
        }
      }, 0);
    }

    disconnectedCallback() {
      document.removeEventListener('cart:updated', this.handleCartUpdate);
      if (this.drawer && typeof this.drawer.off === 'function') {
        this.drawer.off('update-nodes', this.handleCartUpdate);
      }
      this.isSubscribed = false;
    }

    bindDrawer() {
      const drawer = document.querySelector(CART_DRAWER_SELECTOR);
      if (!drawer) {
        return;
      }
      this.drawer = drawer;

      const subscribe = () => {
        if (!this.isConnected || this.isSubscribed || typeof drawer.on !== 'function') {
          return;
        }
        drawer.on('update-nodes', this.handleCartUpdate);
        this.isSubscribed = true;
      };

      if (typeof drawer.on === 'function') {
        subscribe();
      } else {
        window.customElements.whenDefined(CART_DRAWER_SELECTOR).then(subscribe);
      }
    }
  }

  /**
   * Loop Checkout+ return coverage.
   *
   * Loop reports this shop's coverage as a cart line item, so opting in is an
   * add and opting out is a remove — the same shape as the free gift. It lives
   * in the drawer footer, whose children are replaced wholesale on every cart
   * update, so this element is rebuilt each time and binds on connect rather
   * than subscribing to `update-nodes`.
   */
  class CuftCartCoverage extends HTMLElement {
    connectedCallback() {
      this.handleChange = this.handleChange.bind(this);
      this.handleCheckoutClick = this.handleCheckoutClick.bind(this);
      this.handlePageShow = this.handlePageShow.bind(this);
      this.addEventListener('change', this.handleChange);
      // The checkout buttons sit in the footer's button block, a sibling of
      // this element rather than a child, so that listener goes on the document
      // and filters by attribute.
      document.addEventListener('click', this.handleCheckoutClick);
      window.addEventListener('pageshow', this.handlePageShow);
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.handleChange);
      document.removeEventListener('click', this.handleCheckoutClick);
      window.removeEventListener('pageshow', this.handlePageShow);
    }

    /**
     * Backing out of checkout restores this page from the back/forward cache
     * exactly as it was left — spinner showing and isCheckingOut still set —
     * so the buttons would sit loading and ignore every click.
     */
    handlePageShow(event) {
      if (!event.persisted) {
        return;
      }
      this.isCheckingOut = false;
      document
        .querySelectorAll('[data-cuft-coverage-checkout].loading')
        .forEach(button => button.classList.remove('loading'));
    }

    /* ---------------------------------------------------------------- *
     * Two-button checkout
     * ---------------------------------------------------------------- */

    /**
     * Neither button is preselected, so whichever the customer picks is an
     * affirmative choice: the cart is brought in line with it and the form then
     * submits as it always would. Both are real submit buttons, so if this
     * script never loads the customer still reaches checkout — the cart just
     * goes unadjusted.
     */
    handleCheckoutClick(event) {
      const dialog = this.querySelector('[data-cuft-coverage-dialog]');

      if (dialog && event.target.closest('[data-cuft-coverage-info-open]')) {
        event.preventDefault();
        // showModal puts it in the top layer, clear of the drawer's stacking.
        // Older browsers fall back to a plain open, which still shows the text.
        if (typeof dialog.showModal === 'function') {
          dialog.showModal();
        } else {
          dialog.setAttribute('open', '');
        }
        return;
      }

      if (dialog && dialog.open) {
        // The backdrop is part of the dialog, so a click landing on the element
        // itself rather than its body means the customer clicked outside.
        const closing =
          event.target.closest('[data-cuft-coverage-info-close]') || event.target === dialog;
        if (closing) {
          event.preventDefault();
          if (typeof dialog.close === 'function') {
            dialog.close();
          } else {
            dialog.removeAttribute('open');
          }
          return;
        }
      }

      const button = event.target.closest('[data-cuft-coverage-checkout]');
      if (!button) {
        return;
      }

      // Always hold the native submit back. Letting a second click through while
      // a checkout is in flight would reach checkout without the cart being
      // adjusted — a decline that still carries the fee.
      event.preventDefault();
      if (this.isCheckingOut) {
        return;
      }

      this.isCheckingOut = true;
      button.classList.add('loading');

      this.setCoverage(button.dataset.cuftCoverageCheckout === 'accept')
        .catch(() => {
          /* Reaching checkout matters more than the adjustment landing. */
        })
        .then(() => this.submitCheckout(button));
    }

    submitCheckout(button) {
      const form = button.form || document.getElementById('CartDrawerForm');
      if (!form) {
        this.isCheckingOut = false;
        button.classList.remove('loading');
        return;
      }

      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit(button);
        return;
      }

      // Older Safari: submit() ignores the submitter, so the name it carries has
      // to be supplied by hand or the post lands back on the cart.
      const hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = button.name || 'checkout';
      hidden.value = button.value || '';
      form.appendChild(hidden);
      form.submit();
    }

    /* ---------------------------------------------------------------- *
     * Switch layout
     * ---------------------------------------------------------------- */

    handleChange(event) {
      const toggle = event.target.closest('[data-cuft-coverage-toggle]');
      if (!toggle) {
        return;
      }

      // <cart-drawer> reads target.dataset.index off any change event it sees
      // and wedges itself in a loading state when there is none — the same trap
      // the upsell dropdowns fall into.
      event.stopPropagation();

      this.toggleAttribute('is-busy', true);
      toggle.disabled = true;

      this.setCoverage(toggle.checked)
        .then(() => refreshDrawer())
        .catch(() => {
          // Put the switch back where the cart actually is.
          toggle.checked = !toggle.checked;
        })
        .then(() => {
          this.toggleAttribute('is-busy', false);
          toggle.disabled = false;
        });
    }

    /** Bring the cart in line with the choice, adding or removing the fee. */
    setCoverage(wanted) {
      const variantId = Number(this.dataset.variantId || 0);
      const productId = Number(this.dataset.productId || 0);
      if (!variantId) {
        return Promise.resolve();
      }

      return getCart().then(cart => {
        if (!cart || !Array.isArray(cart.items)) {
          return null;
        }

        const lines = cart.items.filter(
          item => item.variant_id === variantId || item.product_id === productId
        );

        if (!wanted) {
          return lines.length ? removeLines(lines) : null;
        }
        return lines.length ? null : addToCart(variantId, { _cuft_coverage: 'true' });
      });
    }
  }

  const components = {
    'cuft-cart-progress-block': CuftCartProgressBlock,
    'cuft-cart-review-block': CuftCartReviewBlock,
    'cuft-cart-upsell-block': CuftCartUpsellBlock,
    'cuft-cart-gift': CuftCartGift,
    'cuft-cart-coverage': CuftCartCoverage,
  };

  Object.keys(components).forEach(tagName => {
    if (!window.customElements.get(tagName)) {
      window.customElements.define(tagName, components[tagName]);
    }
  });
})();
